import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router'
import type { SiteMap, SiteMapLink, SiteMapPage } from '@innerloopco/loopcms-core'
import { Icon } from '../../components/index.js'
import { StatusTag } from '../content/StatusTag.js'
import { buildTree, flatten, initiallyFolded, linksFor } from './tree.js'

export interface SiteMapListProps {
  map: SiteMap
  query: string
}

/**
 * The site as an outline by address (Figma "Site map · List"): one row per
 * page, collections collapsible, counts of links in and out, and a row that
 * opens to show which pages those are and where each link sits. A page's
 * name opens it — in the editor, or on the site for pages LoopCMS doesn't edit.
 */
export function SiteMapList({ map, query }: SiteMapListProps) {
  const tree = useMemo(() => buildTree(map.pages), [map.pages])
  // Big collections start folded, as on the Map.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => initiallyFolded(tree))
  const [open, setOpen] = useState<string | null>(null)
  const titles = useMemo(() => new Map(map.pages.map((p) => [p.path, p.title])), [map.pages])

  const needle = query.trim().toLowerCase()
  const rows = needle
    ? flatten(tree, new Set()).filter(({ node }) => node.page.title.toLowerCase().includes(needle) || node.page.path.toLowerCase().includes(needle)).map(({ node }) => ({ node, depth: 1 }))
    : flatten(tree, collapsed)

  const toggle = (path: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })

  return (
    <table className="lc-sm-table" role="treegrid" aria-label="Site map">
      <thead>
        <tr>
          <th scope="col">Page</th>
          <th scope="col">Address</th>
          <th scope="col">Linked from</th>
          <th scope="col">Links to</th>
          <th scope="col"><span className="sr-only">Links</span></th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ node, depth }) => {
          const { page } = node
          const links = linksFor(map, page.id)
          const isOpen = open === page.path
          const hasChildren = node.children.length > 0 && !needle
          return (
            <Fragment key={page.path}>
              <tr data-path={page.path} aria-level={depth} aria-expanded={hasChildren ? !collapsed.has(page.path) : undefined} className={isOpen ? 'is-open' : undefined}>
                <td>
                  <span className="lc-sm-name" style={{ paddingLeft: (depth - 1) * 22 }}>
                    {hasChildren ? (
                      <button
                        type="button"
                        className="lc-sm-caret"
                        aria-label={`${collapsed.has(page.path) ? 'Expand' : 'Collapse'} ${page.title}`}
                        onClick={() => toggle(page.path)}
                      >
                        <Icon name="CaretDown" size={13} style={{ transform: collapsed.has(page.path) ? 'rotate(-90deg)' : undefined }} />
                      </button>
                    ) : (
                      <span className="lc-sm-caret" aria-hidden />
                    )}
                    <PageLink page={page} siteUrl={map.crawl?.siteUrl ?? null} />
                    {page.kind === 'listing' ? <span className="text-muted lc-sm-count">{node.children.length}</span> : null}
                    {page.status && page.status !== 'published' ? <StatusTag status={page.status} /> : null}
                    {page.noLinksIn ? <span className="lc-sm-nolinks">No links in</span> : null}
                  </span>
                </td>
                <td className="mono lc-sm-path">{page.path}</td>
                <td data-testid="links-in">{links.from.length}</td>
                <td data-testid="links-out">{links.to.length}</td>
                <td className="lc-sm-action">
                  <button type="button" className="btn btn-ghost" aria-expanded={isOpen} aria-label={`Show links for ${page.title}`} onClick={() => setOpen(isOpen ? null : page.path)}>
                    <Icon name={isOpen ? 'CaretDown' : 'ArrowRight'} size={13} />
                  </button>
                </td>
              </tr>
              {isOpen ? (
                <tr className="lc-sm-detail">
                  <td colSpan={5}>
                    <div role="region" aria-label={`Links for ${page.title}`} className="lc-sm-groups" style={{ paddingLeft: (depth - 1) * 22 + 21 }}>
                      <LinkGroup label="Linked from" links={links.from} pick={(l) => l.from} titles={titles} />
                      <LinkGroup label="Links to" links={links.to} pick={(l) => l.to} titles={titles} />
                    </div>
                  </td>
                </tr>
              ) : null}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}

function PageLink({ page, siteUrl }: { page: SiteMapPage; siteUrl: string | null }) {
  if (page.entryId) return <Link to={`/content/${page.entryId}`} className="lc-sm-link">{page.title}</Link>
  if (page.kind === 'site' && siteUrl) {
    return (
      <a href={`${siteUrl}${page.path}`} target="_blank" rel="noopener noreferrer" className="lc-sm-link is-site">
        {page.title}
      </a>
    )
  }
  return <span className="lc-sm-link is-plain">{page.title}</span>
}

function LinkGroup({ label, links, pick, titles }: { label: string; links: SiteMapLink[]; pick: (l: SiteMapLink) => string; titles: Map<string, string> }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      <span className="lc-sm-group-label">{label}</span>
      {links.length === 0 ? (
        <span className="text-muted" style={{ fontSize: 12.5 }}>None</span>
      ) : (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {links.map((link) => (
            <span key={`${link.from}>${link.to}`} className="lc-sm-chip">
              <span>{titles.get(pick(link)) ?? pick(link)}</span>
              <span className="text-muted">{link.where.join(', ')}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
